import type { Author } from '@bulknutrients/bulk-shared-lib/interfaces/Expert';
import {
  CLOUDINARY_ASSET_FIELDS,
  IMAGE_FIELDS,
} from '@bulknutrients/bulk-shared-lib/lib/queries/sanity/snippets';
import { cdnClient as client } from '@bulknutrients/bulk-shared-lib/lib/sanity/client';
import { formatUrl } from '@bulknutrients/bulk-shared-lib/utils';
import { ErrorCard } from '@components/Alerts';
import { LoadMore } from '@components/LoadMore/LoadMore';
import { CustomImage } from '@components/Media/Image/Image';
import { PortableTextBlockRenderer } from '@components/PortableText/PortableText';
import { Paragraph } from '@components/Typography/Paragraph/Paragraph';
import { TextLink } from '@components/Typography/TextLink/TextLink';
import Link from 'next/link';
import type { ReactElement } from 'react';
import useSWRInfinite from 'swr/infinite';

export function AuthorCardList(): ReactElement {
  const PAGE_SIZE = 45;

  // A function to get the SWR key of each page,
  // its return value will be accepted by `fetcher`.
  // If `null` is returned, the request of that page won't start.
  const getKey = (pageIndex, previousPageData) => {
    if (previousPageData && !previousPageData.data.length) return null; // reached the end

    return `/api/author/getAuthorCards?pageIndex=${pageIndex}&pageSize=${PAGE_SIZE}`; // SWR key
  };

  const fetcher = async (url) => {
    const query = `//groq
    {
      "data": *[_type == "author" 
        && !(_id in path("drafts.**")) 
        && defined(slug)
        && profilePage != false
      ] 
      {
        _id,
        name,
        "slug": slug.current,
        "image": select(
          image.asset._type == "cloudinary.asset" => image{
            ${CLOUDINARY_ASSET_FIELDS}
          },
          image._type == "image" => image{
          ${IMAGE_FIELDS}
        }),
        singleLineDescription,
        "shortDescription": shortDescription[]{
          _type == "insertAside" => @->{...},
          _type == "form" => @->{title, formType, klaviyoFormId, shortStackFormId, shortStackV2FormId, shortStackFormHeight},
          _type == "image" => {
            ${IMAGE_FIELDS}
          },
          _type == "cloudinaryAsset" => @{
            ${CLOUDINARY_ASSET_FIELDS}
          },
          ...,
          markDefs[]{
            ...,
            _type == "internalLink" => {
              "slug": @.item->slug,
            }
          }
        },
        facebook,
        instagram,
        twitter,
        tiktok,
        linkedIn,
        website,
      } [$start...$end],
      "count": count(*[_type == "author" 
        && !(_id in path("drafts.**")) 
        && defined(slug)
        && profilePage != false
      ]),
    }
    `;

    const pageIndex = new URL(url, 'https://www.bulk.com').searchParams.get(
      'pageIndex'
    );

    const start = Number.parseInt(pageIndex as string) * PAGE_SIZE;
    const end = start + PAGE_SIZE;

    try {
      const { data, count }: { data: Author[]; count: number } =
        await client.fetch(query, {
          start,
          end,
        });
      data.forEach((author) => (author.slug = formatUrl(author.slug)));
      return { data, count };
    } catch (err) {
      const error = new Error('Unable to fetch authors');
      throw error;
    }
  };

  const { data, error, isLoading, size, setSize } = useSWRInfinite(
    getKey,
    fetcher
  );

  const authors: Array<Author> = data ? [...data.flatMap((d) => d.data)] : [];

  const isLoadingMore =
    isLoading || (size > 0 && data && typeof data[size - 1] === 'undefined');
  const isEmpty = data?.[0]?.data.length === 0;
  const isReachingEnd =
    isEmpty || (data && data[data.length - 1]?.data.length < PAGE_SIZE);

  return (
    <>
      {error && <ErrorCard title={error.name} message={error.message} />}
      {authors && (
        <section className="mb-8 grid justify-items-center grid-cols-1 gap-9 sm:grid-cols-2 lg:grid-cols-3">
          {(authors.flat() as Author[]).map((author, idx) => {
            const slug =
              author.slug[0] === '/' ? author.slug : `/${author.slug}`;

            return (
              <article
                key={`author${idx}`}
                className="card flex max-w-xs flex-col sm:max-w-sm rounded-2xl overflow-hidden dark:bg-grey-dark transition-all duration-300 hover:scale-103"
              >
                {author.image && (
                  <Link
                    prefetch={process.env.NEXT_PUBLIC_PREFETCH === 'true'}
                    href={slug}
                  >
                    <CustomImage
                      image={author.image}
                      width={400}
                      height={400}
                      className="rounded-none"
                    />
                  </Link>
                )}
                <div className="p-4 pb-0 grow">
                  <TextLink href={slug} clasName="group">
                    <h2 className="font-secondary text-2xl uppercase text-grey-dark group-hover:text-orange dark:text-white">
                      {author.name}
                    </h2>
                  </TextLink>
                  {author.singleLineDescription ? (
                    <Paragraph>{author.singleLineDescription}</Paragraph>
                  ) : (
                    author.shortDescription && (
                      <PortableTextBlockRenderer
                        content={author.shortDescription}
                      />
                    )
                  )}
                </div>
              </article>
            );
          })}
        </section>
      )}
      <LoadMore
        clickHandler={() => setSize(size + 1)}
        disabled={(isLoadingMore || isReachingEnd) ?? false}
        text={
          isLoadingMore
            ? 'loading...'
            : isReachingEnd
              ? 'no more results'
              : 'load more'
        }
        viewedResults={authors.length ?? 0}
        totalResults={data?.[0]?.count}
      />
    </>
  );
}
